import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Refresh, CheckCircle, Warning, Error as ErrorIcon } from "@mui/icons-material";
import { useRecordContext, useNotify } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import type { ProviderHealthDto } from "../../../types/api";

export const ProviderHealth = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [health, setHealth] = useState<ProviderHealthDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (record?.id) {
      loadHealth();
    }
  }, [record?.id]);

  const loadHealth = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const response = await fetch(
        apiUrl(`/providers/${record.id}/health`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setHealth(data);
      } else {
        throw new Error("Failed to load health data");
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle color="success" />;
      case "degraded":
        return <Warning color="warning" />;
      case "down":
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "success";
      case "degraded":
        return "warning";
      case "down":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Health Status</Typography>
        <Button
          startIcon={<Refresh />}
          onClick={loadHealth}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : health ? (
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {getStatusIcon(health.status)}
                  <Chip
                    label={health.status.toUpperCase()}
                    color={getStatusColor(health.status)}
                    size="medium"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Last checked: {new Date(health.lastCheck).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>

              {health.responseTime !== undefined && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Response Time
                  </Typography>
                  <Typography variant="h6">
                    {health.responseTime}ms
                  </Typography>
                </Grid>
              )}

              {health.uptime !== undefined && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Uptime
                  </Typography>
                  <Typography variant="h6">
                    {Math.round(health.uptime * 100) / 100}%
                  </Typography>
                </Grid>
              )}

              {health.errorRate !== undefined && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Error Rate
                  </Typography>
                  <Typography variant="h6">
                    {Math.round(health.errorRate * 100) / 100}%
                  </Typography>
                </Grid>
              )}

              {health.details && Object.keys(health.details).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Additional Details
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      overflow: "auto",
                      fontSize: "0.875rem",
                    }}
                  >
                    {JSON.stringify(health.details, null, 2)}
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Typography color="text.secondary">
          No health data available
        </Typography>
      )}
    </Box>
  );
};
